import type { Middleware } from 'openapi-fetch'

import { NetworkError } from '@/api/errors'
import { readErrorCode } from '@/api/error-body'
import { getSession } from '@/features/auth/session-store'
import { endSessionLocally } from '@/features/auth/refresh'

/**
 * A request that never got a response.
 *
 * Not an API error at all -- there is no status and no code to interpret -- and it
 * arrives from `fetch` as a bare `TypeError: Failed to fetch`, which is not
 * something to show anyone. Applied to every client including the one the refresh
 * endpoint uses, because "the backend is unreachable" is exactly what the sign-in
 * page needs to tell apart from "your session was refused".
 */
export const networkErrorMiddleware: Middleware = {
  onError({ error }) {
    return error instanceof NetworkError ? error : new NetworkError(error)
  },
}

/**
 * A session whose organisation has no active subscription, which every request it
 * makes will be refused for. Sent to the page that says so rather than to `/login`,
 * which would forward to the realm and straight back.
 */
export const subscriptionRequiredMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.status !== 402) return undefined

    const code = await readErrorCode(response.clone())
    if (code === 'ERROR_SUBSCRIPTION_REQUIRED' && getSession().authenticated) {
      endSessionLocally()
      window.location.replace('/login/error?error=errorSubscriptionRequired')
    }

    return undefined
  },
}

/**
 * A refresh token the backend has rejected, discovered by some other request.
 *
 * The session is over whichever request found out; leaving it in place would have
 * every subsequent call fail the same way while the UI still showed a signed-in
 * shell. Not applied to the bare client: a refresh that is itself refused is
 * handled where it is made, with the token that was tried in hand.
 */
export const sessionEndedMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.status !== 401) return undefined

    const code = await readErrorCode(response.clone())
    if (code === 'ERROR_INVALID_REFRESH_TOKEN' && getSession().authenticated) {
      // The realm's own session is left alone: losing a token here is not the
      // user asking to be signed out, and `/login` forwards straight back to the
      // provider, which answers a live single sign-on session with a new one.
      endSessionLocally()
    }

    return undefined
  },
}
